import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import toast from 'react-hot-toast';
import { FaCheckDouble, FaTasks } from 'react-icons/fa';
import DatePicker from "../../../shared/DatePicker";

import TextInput from '../../../shared/TextInput';
import DropdownField from '../../../shared/DropdownField';
import CustomButton from '../../../shared/CustomButton';
import { addTask } from '../../../redux/slices/WardManagementSlice';
import { useTranslation } from 'react-i18next';
import CheckPermission from '../../../hooks/CheckPermission';
const addTaskValidationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .min(2, 'Title must be at least 2 characters')
    .max(100, 'Title must be less than 100 characters'),
  description: Yup.string().required('Description is required').min(5, 'Description must be at least 5 characters'),
  priority: Yup.string().required('Priority is required'),
  patientId: Yup.string().required('Patient selection is required'),
  date: Yup.date().required('Date is required'),
  status: Yup.string().required('Status is required'),
  dueDate: Yup.date().required('Due date is required')
});

const AddTaskDialog = ({ visible, onHide, wardId, wardPatients = [] }) => {
  const { hasPermission } = CheckPermission();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const initialValues = {
    title: '',
    description: '',
    priority: 'Medium',
    patientId: '',
    date: new Date(),
    status: 'Pending',
    dueDate: ''
  };

  const priorityOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  const statusOptions = [
    { label: 'Pending', value: 'Pending' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' }
  ];

  const patientOptions = wardPatients.map(patient => ({
    label: `${patient.full_name} (${patient.file_no})`,
    value: patient.patient_id
  }));

  const handleSubmit = async (values, { resetForm }) => {
    if (!hasPermission('ward', 'create')) return;
    setIsLoading(true);
    try {
      console.log('Ward ID:', wardId);
      console.log('Task payload:', {
        title: values.title,
        description: values.description,
        priority: values.priority,
        patientId: values.patientId,
        wardId: wardId,
        date: values.date,
        status: values.status,
        dueDate: values.dueDate
      });

      dispatch(
        addTask({
          title: values.title,
          description: values.description,
          priority: values.priority,
          patientId: values.patientId,
          wardId: wardId,
          date: values.date,
          status: values.status,
          dueDate: values.dueDate
        })
      );

      toast.success('Task added successfully!');
      resetForm();
      onHide();
    } catch (error) {
      console.log('Error adding task:', error);
      toast.error('Failed to add task');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={
        <div className='flex items-center gap-2'>
          <FaTasks className='text-primary' />
          <span>{t('Add New Task')}</span>
        </div>
      }
      style={{ width: '600px' }}
      modal
      className='p-fluid'
    >
      {wardPatients.length === 0 ? (
        <div className='text-center py-8'>
          <p className='text-gray-500 mb-4'>{t('No patients available in this ward. Please add patients first.')}</p>
        </div>
      ) : (
        <Formik initialValues={initialValues} validationSchema={addTaskValidationSchema} onSubmit={handleSubmit}>
          {props => (
            <Form>
              <div className='space-y-4'>
                <TextInput name='title' label={t('Task Title*')} placeholder={t('Enter task title')} />

                <TextInput
                  name='description'
                  label={t('Description*')}
                  placeholder={t('Enter task description')}
                  multiline
                  rows={3}
                />

                <div className='grid grid-cols-2 gap-4'>
                  <DropdownField
                    name='priority'
                    label={t('Priority*')}
                    placeholder={t('Select priority')}
                    options={priorityOptions}
                    optionLabel='label'
                    optionValue='value'
                  />

                  <DropdownField
                    name='status'
                    label={t('Status*')}
                    placeholder={t('Select status')}
                    options={statusOptions}
                    optionLabel='label'
                    optionValue='value'
                  />
                </div>

                <DropdownField
                  name='patientId'
                  label={t('Select Patient*')}
                  placeholder={t('Select patient')}
                  options={patientOptions}
                  optionLabel='label'
                  optionValue='value'
                />

                <div className='grid grid-cols-2 gap-4'>
                  <DatePicker name='date' label={t('Date*')} dateFormat='dd/mm/yy' placeholder={t('Select date')} />
                  <DatePicker name='dueDate' label={t('Due Date*')} dateFormat='dd/mm/yy' placeholder={t('Select due date')} />
                </div>

                <div className='flex justify-end gap-3 mt-6 pt-4 border-t'>
                  <CustomButton label={t('Cancel')} buttonType='reset' onClick={onHide} />
                  <CustomButton
                    type='submit'
                    label={t('Add Task')}
                    icon={<FaCheckDouble size={15} className='me-2' />}
                    buttonType='primary'
                    loading={isLoading}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default AddTaskDialog;
